import { useRef, useState } from "react";

import {
    FieldHelperText,
    useAuthController,
    type FieldProps,
} from "@firecms/core";
import { LoadingButton, TextField, UploadIcon } from "@firecms/ui";

import {
    MAX_UPLOAD_BYTES,
    UploadError,
    uploadImage,
    type UploadErrorCode,
} from "../lib/uploadImage";

export interface ImageUploadStrings {
    button: string;
    /** Replaces the button label while bytes are going out. */
    uploading: (percent: number) => string;
    placeholder: string;
    /** Alt text for the thumbnail. */
    preview: string;
    errors: {
        tooLarge: (limitMb: number) => string;
        notAnImage: string;
        notConfigured: string;
        notAllowed: string;
        generic: string;
    };
}

/**
 * A URL string field with an upload button beside it.
 *
 * **Why not FireCMS's own storage field.** `StringProperty.storage` renders a
 * dropzone that *replaces* the text input, so an editor could no longer paste
 * a URL — and news images are not all ours: an article about a partner may
 * legitimately point at an image hosted elsewhere. This keeps both, and an
 * uploaded URL stays hand-editable afterwards.
 *
 * That choice also means the CMS needs no `StorageSource`: `uploadImage` talks
 * to the Pages Function directly, and the value is a plain URL exactly as
 * `ArticleSchema.img` describes. Nothing about the stored shape changes.
 */
export function ImageUploadField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    isSubmitting,
    includeDescription,
    customProps,
}: FieldProps<string, ImageUploadStrings>) {
    const authController = useAuthController();
    const inputRef = useRef<HTMLInputElement>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const strings = customProps;
    const busy = progress !== null;
    const locked = disabled || isSubmitting || busy;

    const messageFor = (code: UploadErrorCode): string => {
        switch (code) {
            case "too_large":
                return strings.errors.tooLarge(MAX_UPLOAD_BYTES / 1024 / 1024);
            case "not_an_image":
                return strings.errors.notAnImage;
            case "not_configured":
                return strings.errors.notConfigured;
            case "missing_token":
            case "not_a_one_ai_account":
                return strings.errors.notAllowed;
            default:
                return strings.errors.generic;
        }
    };

    const onFileChosen = async (file: File) => {
        // Checked here as well as in the function: a 10 MB limit is worth
        // enforcing before the bytes go over the wire, not after.
        if (!file.type.startsWith("image/")) {
            setUploadError(messageFor("not_an_image"));
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            setUploadError(messageFor("too_large"));
            return;
        }

        setUploadError(null);
        setProgress(0);
        try {
            const token = await authController.getAuthToken();
            const url = await uploadImage({ file, token, onProgress: setProgress });
            setValue(url);
        } catch (err) {
            console.error("[upload] image upload failed", err);
            setUploadError(
                messageFor(
                    err instanceof UploadError ? err.code : "unknown",
                ),
            );
        } finally {
            setProgress(null);
        }
    };

    return (
        <>
            <div className="flex items-start gap-2">
                <TextField
                    className="flex-grow"
                    type="url"
                    value={value ?? ""}
                    onChange={(event) =>
                        setValue(event.target.value.trim() || null)
                    }
                    label={property.name}
                    placeholder={strings.placeholder}
                    error={showError}
                    disabled={locked}
                />

                <LoadingButton
                    className="mt-1 shrink-0"
                    variant="outlined"
                    startIcon={<UploadIcon size="small" />}
                    loading={busy}
                    disabled={locked}
                    onClick={() => inputRef.current?.click()}
                >
                    {busy
                        ? strings.uploading(progress ?? 0)
                        : strings.button}
                </LoadingButton>

                {/*
                 * The real control is the button above; this input only exists
                 * to open the file picker. A visible file input cannot be
                 * styled to match, and `hidden` keeps it out of the tab order.
                 */}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        // Cleared so choosing the same file twice in a row —
                        // after a failure, typically — fires `change` again.
                        event.target.value = "";
                        if (file) void onFileChosen(file);
                    }}
                />
            </div>

            {value && (
                // apps/cms is a Vite SPA, so there is no `next/image` to reach
                // for — the rule arrives through the shared eslint-config-next
                // base. See the same disable in apps/one-ai's NewsDetail.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={value}
                    alt={strings.preview}
                    className="mt-2 max-h-40 rounded-md border border-surface-200 dark:border-surface-700"
                />
            )}

            {uploadError && (
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                    {uploadError}
                </p>
            )}

            <FieldHelperText
                includeDescription={includeDescription}
                showError={showError}
                error={error}
                property={property}
            />
        </>
    );
}
